// src/components/AppShell.jsx
import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Point directly to your page components
import HomeSection from "../pages/Home";
import ProjectsSection from "../pages/Projects";
import ResumeSection from "../pages/Resume";
import ContactSection from "../pages/Contact";

const sections = [
  { id: "home",     path: "/" },
  { id: "projects", path: "/projects" },
  { id: "resume",   path: "/resume" },
  { id: "contact",  path: "/contact" },
];

export default function AppShell() {
  const sectionRefs = useRef({});
  const ioRef = useRef(null);
  const location = useLocation();

  const getNavHeight = () => {
    const v = getComputedStyle(document.documentElement).getPropertyValue("--nav-h") || "72";
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 72;
  };

  // 1) On route change, scroll to the matching section (with navbar offset)
  useEffect(() => {
    const match = sections.find(s => s.path === location.pathname) || sections[0];
    const el = sectionRefs.current[match.id];
    if (!el) return;

    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const t = setTimeout(() => {
      const navH = getNavHeight();
      const rect = el.getBoundingClientRect();
      const y = window.scrollY + rect.top - navH;
      window.scrollTo({ top: y, behavior: prefersReduced ? "auto" : "smooth" });
    }, 30); // let layout paint

    return () => clearTimeout(t);
  }, [location.pathname]);

  // 2) While scrolling, update the URL bar WITHOUT navigating/re-rendering
  useEffect(() => {
    const navH = getNavHeight();

    ioRef.current?.disconnect?.();
    ioRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;

        const id = visible.target.id;
        const sec = sections.find(s => s.id === id);
        if (!sec) return;

        if (window.location.pathname !== sec.path) {
          window.history.replaceState(null, "", sec.path); // no React rerender
        }
      },
      {
        root: null,
        rootMargin: `-${navH}px 0px 0px 0px`, // account for sticky navbar
        threshold: [0.5],                     // "active" when ≥50% visible
      }
    );

    sections.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (el) ioRef.current.observe(el);
    });

    return () => ioRef.current?.disconnect?.();
  }, []); // intentionally no deps

  return (
    <main>
      <section
        id="home"
        ref={el => (sectionRefs.current.home = el)}
        className="page-section"
        style={{ padding: 0 }}
      >
        <HomeSection />
      </section>

      <section
        id="projects"
        ref={el => (sectionRefs.current.projects = el)}
        className="page-section"
      >
        <ProjectsSection />
      </section>

      <section
        id="resume"
        ref={el => (sectionRefs.current.resume = el)}
        className="page-section"
      >
        <ResumeSection />
      </section>

      <section
        id="contact"
        ref={el => (sectionRefs.current.contact = el)}
        className="page-section"
      >
        <ContactSection />
      </section>

      {/* Local styles for spacing + smooth behavior */}
      <style>{`
        html { scroll-behavior: smooth; }
        :root { --nav-h: 72px; } /* keep in sync with your Navbar height */

        .page-section {
          min-height: 100vh;
          display: center;
          align-items: center;
          padding: calc(var(--nav-h) + 24px) 1.5rem 4rem;
          color: #fff;
          scroll-margin-top: var(--nav-h); /* for browsers that honor it */
        }

        @media (prefers-reduced-motion: reduce) {
          html { scroll-behavior: auto; }
        }
      `}</style>
    </main>
  );
}




